import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Brain, CheckCircle2, Lock, ArrowLeft, BookOpen, Layers, ChevronRight, ClipboardList } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import LearningStyleBadge from "../../components/LearningStyleBadge";
import { toast } from "sonner";

export default function Lecciones() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const courseParam = params.get("course");
    const unitId = params.get("unit");
    const [units, setUnits] = useState([]);

    useEffect(() => {
        api.get("/lessons/units").then((r) => setUnits(r.data || []));
    }, []);

    // Group units by subject (this is the COURSE level)
    const courses = useMemo(() => {
        const map = {};
        units.forEach((u) => {
            if (!map[u.subject]) map[u.subject] = [];
            map[u.subject].push(u);
        });
        // Sort units inside each course by unit_number
        Object.values(map).forEach((list) =>
            list.sort((a, b) => (a.unit_number || 0) - (b.unit_number || 0))
        );
        return map;
    }, [units]);

    const selectedUnit = unitId ? units.find((u) => u.id === unitId) : null;

    if (selectedUnit) {
        if (selectedUnit.locked) {
            toast.message("Esta unidad aún no está disponible. Tu profesor la activará pronto.");
            setParams({});
            return null;
        }
        return (
            <UnitLessons
                unit={selectedUnit}
                onBack={() => setParams({ course: selectedUnit.subject })}
            />
        );
    }

    // Selected course → show its units
    if (courseParam && courses[courseParam]) {
        return (
            <CourseDetail
                courseName={courseParam}
                units={courses[courseParam]}
                userGrade={user.grade}
                onBack={() => setParams({})}
                onPickUnit={(u) => {
                    if (u.locked) {
                        toast.message(
                            "Esta unidad aún no está disponible. Tu profesor la activará pronto.",
                            { duration: 4000 }
                        );
                    } else {
                        setParams({ unit: u.id });
                    }
                }}
            />
        );
    }

    // Default: Course blocks grid
    const courseNames = Object.keys(courses).sort();
    return (
        <div className="p-6 md:p-10 fade-in" data-testid="lecciones-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">Biblioteca de Lecciones</h1>
                    <p className="text-edu-body mt-1">
                        Tus cursos de {user.grade || "tu grado"}. Selecciona un curso para ver sus unidades.
                    </p>
                </div>
                <div className="inline-flex items-center gap-2 bg-edu-card border border-edu rounded-full px-3 py-2 text-sm">
                    <Brain size={16} className="text-edu-navy" />
                    <span className="text-edu-muted">Tu estilo:</span>
                    <LearningStyleBadge style={user.learning_style} />
                </div>
            </div>

            {courseNames.length === 0 && (
                <div className="edu-card p-10 text-center text-edu-muted" data-testid="empty-courses">
                    No hay cursos disponibles para tu grado aún.
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="course-blocks">
                {courseNames.map((name) => {
                    const unitsOfCourse = courses[name];
                    const totalLessons = unitsOfCourse.reduce(
                        (acc, u) => acc + (u.lesson_count || 0),
                        0
                    );
                    const completedLessons = unitsOfCourse.reduce(
                        (acc, u) => acc + (u.completed_count || 0),
                        0
                    );
                    const pendingAssignments = unitsOfCourse.reduce(
                        (acc, u) => acc + (u.pending_assignments_count || 0),
                        0
                    );
                    const progress = totalLessons
                        ? Math.round((completedLessons / totalLessons) * 100)
                        : 0;
                    const activeUnits = unitsOfCourse.filter((u) => !u.locked).length;
                    return (
                        <button
                            key={name}
                            onClick={() => setParams({ course: name })}
                            className="edu-card p-6 text-left group hover:shadow-lg transition-shadow relative overflow-hidden"
                            data-testid={`course-block-${name}`}
                        >
                            <div
                                className="absolute top-0 left-0 right-0 h-1.5"
                                style={{ background: "var(--edu-navy)" }}
                            />
                            {pendingAssignments > 0 && (
                                <span
                                    className="absolute top-3 right-3 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-semibold inline-flex items-center justify-center shadow"
                                    title={`${pendingAssignments} tarea${pendingAssignments !== 1 ? "s" : ""} pendiente${pendingAssignments !== 1 ? "s" : ""}`}
                                    data-testid={`pending-badge-${name}`}
                                >
                                    {pendingAssignments}
                                </span>
                            )}
                            <div className="flex items-start justify-between mb-3">
                                <div className="w-12 h-12 rounded-xl bg-edu-navy/10 flex items-center justify-center">
                                    <BookOpen size={22} className="text-edu-navy" />
                                </div>
                                <ChevronRight
                                    size={18}
                                    className="text-edu-muted group-hover:text-edu-navy transition-colors"
                                />
                            </div>
                            <h2 className="text-xl font-semibold mb-1">{name}</h2>
                            <p className="text-xs text-edu-muted mb-1">
                                {unitsOfCourse.length}{" "}
                                {unitsOfCourse.length === 1 ? "unidad" : "unidades"} ·{" "}
                                {activeUnits} disponibles · {totalLessons} lecciones
                            </p>
                            {pendingAssignments > 0 && (
                                <p className="text-xs text-amber-700 font-medium mb-3 inline-flex items-center gap-1">
                                    <ClipboardList size={12} />
                                    {pendingAssignments} tarea{pendingAssignments !== 1 ? "s" : ""} pendiente{pendingAssignments !== 1 ? "s" : ""}
                                </p>
                            )}
                            <div className={`flex items-center justify-between gap-3 ${pendingAssignments > 0 ? "" : "mt-4"}`}>
                                <div className="flex-1">
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-emerald-500 transition-all"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                                <span className="text-xs text-edu-muted font-medium">
                                    {progress}%
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function CourseDetail({ courseName, units, userGrade, onBack, onPickUnit }) {
    return (
        <div className="p-6 md:p-10 fade-in" data-testid="course-detail-page">
            <button
                onClick={onBack}
                className="inline-flex items-center gap-2 text-sm text-edu-navy hover:underline mb-4"
                data-testid="back-to-courses"
            >
                <ArrowLeft size={14} /> Volver a cursos
            </button>
            <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-edu-navy/10 flex items-center justify-center">
                    <BookOpen size={22} className="text-edu-navy" />
                </div>
                <div>
                    <h1 className="text-3xl font-semibold">{courseName}</h1>
                    <p className="text-edu-body">
                        Unidades de {userGrade}. Selecciona una para abrir sus lecciones.
                    </p>
                </div>
            </div>
            <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="course-units-grid"
            >
                {units.map((u) => (
                    <UnitCard key={u.id} unit={u} onClick={() => onPickUnit(u)} />
                ))}
            </div>
        </div>
    );
}

function UnitCard({ unit, onClick }) {
    const locked = unit.locked;
    const pendingAssignments = unit.pending_assignments_count || 0;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`edu-card p-5 text-left transition group relative overflow-hidden ${
                locked ? "opacity-70" : "hover:shadow-lg"
            }`}
            data-testid={`unit-card-${unit.id}`}
        >
            <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{ background: locked ? "#9CA3AF" : "var(--edu-navy)" }}
            />
            {locked ? (
                <div className="absolute top-3 right-3">
                    <Lock size={16} className="text-edu-muted" />
                </div>
            ) : pendingAssignments > 0 ? (
                <span
                    className="absolute top-3 right-3 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-semibold inline-flex items-center justify-center shadow"
                    title={`${pendingAssignments} tarea${pendingAssignments !== 1 ? "s" : ""} pendiente${pendingAssignments !== 1 ? "s" : ""}`}
                >
                    {pendingAssignments}
                </span>
            ) : null}
            <div className="flex items-center gap-2 mb-2">
                <span className="edu-pill bg-edu-navy/10 text-edu-navy">U{unit.unit_number}</span>
                {locked ? (
                    <span className="edu-pill bg-gray-100 text-gray-700">Próximamente</span>
                ) : (
                    <span className="edu-pill bg-emerald-100 text-emerald-800">Activa</span>
                )}
            </div>
            <h3 className={`text-base font-semibold mb-1 ${locked ? "text-edu-muted" : ""}`}>
                {unit.unit_name}
            </h3>
            <p className="text-xs text-edu-muted mb-3">
                {unit.lesson_count} {unit.lesson_count === 1 ? "lección" : "lecciones"}
            </p>
            <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-emerald-500 transition-all"
                            style={{ width: `${unit.progress_percent}%` }}
                        />
                    </div>
                </div>
                <span className="text-xs text-edu-muted font-medium w-14 text-right">
                    {unit.completed_count}/{unit.lesson_count}
                </span>
            </div>
        </button>
    );
}

function UnitLessons({ unit, onBack }) {
    const [lessons, setLessons] = useState([]);
    useEffect(() => {
        api.get("/lessons", {
            params: { subject: unit.subject, unit_number: unit.unit_number },
        }).then((r) => setLessons(r.data || []));
    }, [unit.id, unit.subject, unit.unit_number]);

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="unit-lessons-page">
            <button
                onClick={onBack}
                className="inline-flex items-center gap-2 text-sm text-edu-navy hover:underline mb-4"
                data-testid="back-to-units"
            >
                <ArrowLeft size={14} /> Volver a unidades
            </button>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3">
                <div>
                    <div className="inline-flex items-center gap-2 mb-2">
                        <span className="edu-pill bg-edu-gold text-[#0A0A0A]">
                            Unidad {unit.unit_number}
                        </span>
                        <span className="edu-pill bg-edu-navy/10 text-edu-navy">{unit.subject}</span>
                    </div>
                    <h1 className="text-3xl font-semibold">{unit.unit_name}</h1>
                    <p className="text-edu-body mt-1">
                        {unit.lesson_count} lecciones · {unit.completed_count} completadas
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {lessons.length === 0 && (
                    <p className="text-edu-muted col-span-full">No hay lecciones en esta unidad.</p>
                )}
                {lessons.map((l) => (
                    <Link
                        key={l.id}
                        to={`/alumno/lecciones/${l.id}`}
                        className="edu-card p-6 hover:shadow-lg transition-shadow group h-full flex flex-col"
                        data-testid={`lesson-card-${l.id}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex flex-wrap gap-2">
                                <span className="edu-pill bg-edu-navy/10 text-edu-navy">
                                    {l.subject}
                                </span>
                                <LearningStyleBadge style={l.learning_style} />
                            </div>
                            {l.completed && (
                                <span
                                    className="text-emerald-600 inline-flex items-center gap-1 text-xs font-medium"
                                    data-testid={`lesson-completed-${l.id}`}
                                >
                                    <CheckCircle2 size={16} /> Completada
                                </span>
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-edu-text mb-2 group-hover:text-edu-navy">
                            {l.title}
                        </h3>
                        <p className="text-sm text-edu-body mb-5 line-clamp-2 flex-1">
                            {l.description}
                        </p>
                        <div className="flex items-center justify-end">
                            <span className="edu-btn-primary px-4 py-2 text-sm font-medium">
                                Ver Lección
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
